import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useRef, useState } from 'react';

import { useAppDispatch } from '@/app/store/hooks';
import { useCreateSignatureMutation } from '@/features/annual-review';
import { toastShown } from '@/features/ui';

import { SignatureCanvas, type SignatureCanvasHandle } from './SignatureCanvas';
import {
  applyNameStyle,
  renderTypedSignature,
  type NameStyle,
  type SignatureFont,
} from './typedSignature';
import { TypedSignatureFields } from './TypedSignatureFields';
import { useCurrentUser } from './useCurrentUser';

type Mode = 'draw' | 'type';

interface AddSignatureDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Creates a saved signature, by drawing or by typing.
 *
 * The old portal only ever created signatures as a side effect of signing a
 * document, behind a "save this for next time" checkbox. That left a director
 * who removed their last signature with no way to make another without finding
 * something to sign, so this exists as a first-class action.
 */
export const AddSignatureDialog = ({ open, onClose }: AddSignatureDialogProps) => {
  const dispatch = useAppDispatch();
  const { fullName } = useCurrentUser();
  const [createSignature, { isLoading: isSaving }] = useCreateSignatureMutation();

  const [mode, setMode] = useState<Mode>('draw');
  const canvasRef = useRef<SignatureCanvasHandle>(null);
  const [hasDrawn, setHasDrawn] = useState(false);

  const [nameStyle, setNameStyle] = useState<NameStyle>('fullName');
  const [font, setFont] = useState<SignatureFont>('greatVibes');
  // Seeded from the director's record but editable: ASIC's spelling is not
  // always how someone signs, and a company officeholder has no personal name
  // on file at all.
  const [typedName, setTypedName] = useState('');

  const effectiveName = typedName.trim() === '' ? fullName : typedName;
  const previewText = applyNameStyle(effectiveName, nameStyle);
  const canSave = mode === 'draw' ? hasDrawn : previewText !== '';

  // Why the button is disabled, said in words. A control that greys out with no
  // explanation leaves the director guessing — and on a phone the canvas may be
  // below the fold, so "nothing drawn yet" is not self-evident from the screen.
  const blockedReason =
    canSave || isSaving
      ? undefined
      : mode === 'draw'
        ? 'Draw your signature to continue.'
        : 'Enter a name to continue.';

  const reset = () => {
    canvasRef.current?.clear();
    setHasDrawn(false);
    setTypedName('');
    setNameStyle('fullName');
    setFont('greatVibes');
    setMode('draw');
  };

  const handleClose = () => {
    if (isSaving) return;
    reset();
    onClose();
  };

  const handleSave = async () => {
    const image =
      mode === 'draw'
        ? (canvasRef.current?.toDataUrl() ?? null)
        : renderTypedSignature(previewText, font);

    // `canSave` already gates the empty case, so reaching here with no image
    // means the drawing could not be reduced to fit the storage limit — a
    // detailed scribble rather than a blank pad.
    if (image === null) {
      dispatch(
        toastShown('That signature is too detailed to save. Try a simpler one.', 'error'),
      );
      return;
    }

    try {
      await createSignature({ signature: image }).unwrap();
      dispatch(toastShown('Signature saved.', 'success'));
      reset();
      onClose();
    } catch {
      dispatch(toastShown('That signature could not be saved.', 'error'));
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Add a signature</DialogTitle>

      <DialogContent dividers className="flex flex-col gap-4">
        <Tabs
          value={mode}
          onChange={(_event, next: Mode) => {
            setMode(next);
          }}
          variant="fullWidth"
          aria-label="How to create the signature"
        >
          <Tab value="draw" label="Draw" />
          <Tab value="type" label="Type" />
        </Tabs>

        {mode === 'draw' ? (
          <div className="flex flex-col gap-2">
            <Typography variant="body2" color="text.secondary">
              Sign in the box below using your finger or a stylus.
            </Typography>

            <SignatureCanvas
              ref={canvasRef}
              onDrawnChange={setHasDrawn}
              ariaLabel="Signature drawing area"
            />

            <div className="flex justify-end">
              <Button
                onClick={() => {
                  canvasRef.current?.clear();
                }}
                disabled={!hasDrawn || isSaving}
                size="small"
              >
                Clear
              </Button>
            </div>
          </div>
        ) : (
          <TypedSignatureFields
            fullName={fullName}
            typedName={typedName}
            onTypedNameChange={setTypedName}
            nameStyle={nameStyle}
            onNameStyleChange={setNameStyle}
            font={font}
            onFontChange={setFont}
            previewText={previewText}
          />
        )}
      </DialogContent>

      {blockedReason !== undefined && (
        <Typography
          variant="caption"
          color="text.secondary"
          role="status"
          className="px-4 text-center sm:text-right"
        >
          {blockedReason}
        </Typography>
      )}

      <DialogActions className="flex-col-reverse gap-2 p-4 pt-2 sm:flex-row sm:justify-end">
        <Button onClick={handleClose} disabled={isSaving} className="w-full sm:w-auto">
          Cancel
        </Button>
        <Button
          onClick={() => void handleSave()}
          variant="contained"
          disabled={!canSave || isSaving}
          className="w-full sm:w-auto"
        >
          {isSaving ? 'Saving…' : 'Save signature'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
